<template>
  <v-hover v-slot="{ hover }">
    <v-card class="kanban-card px-0 py-1 mt-2" :class="{ 'elevation-2': hover }" outlined :ref="`card-${item.id}`">
      <v-toolbar flat dense color="transparent" height="34">
        <v-toolbar-title class="overflow-visible">
          <v-chip class="ml-n1 pl-1 pr-4" :color="statusStyle.color" label :text-color="statusStyle.textColor" small>
            <v-icon class="mx-0" size="36" style="max-width: 18px;">mdi-circle-small</v-icon>{{statusStyle.label}}
          </v-chip>
        </v-toolbar-title>

        <v-spacer />

        <v-menu v-if="actions.length" close-on-click close-on-content-click left offset-y :nudge-bottom="6" transition="slide-y-transition">
          <template v-slot:activator="{ on, attrs }">
            <v-btn class="ma-0" v-bind="attrs" v-on="on" icon small><v-icon color="secondary" size="24">mdi-dots-horizontal</v-icon></v-btn>
          </template>
          <v-list class="pa-0" dense>
            <v-list-item v-for="a in actions" :key="a.id" @click="$emit('action', { action: a.id, item: item })" :ripple="false">
              <v-list-item-title class="text-right subtitle-2 font-weight-medium" :class="{ 'red--text': a.destructive }">{{a.label}}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-toolbar>

      <v-list-item class="px-3 py-1">
        <v-list-item-content class="py-1">
          <v-list-item-subtitle v-if="typeLabel" class="subtitle-2 font-weight-medium text--secondary">{{typeLabel}}</v-list-item-subtitle>
          <v-list-item-subtitle v-if="item.serial" class="caption text--primary">N° {{item.serial}}</v-list-item-subtitle>
          <v-list-item-title class="mt-2 caption text--secondary">{{customerName}}</v-list-item-title>
        </v-list-item-content>
      </v-list-item>

      <v-divider class="mx-3" />

      <v-card-actions class="py-2 px-3">
        <span v-if="dateLabel" class="caption text--primary">
          <v-icon class="mr-1" color="secondary" size="15">mdi-calendar</v-icon>{{dateLabel}} {{dateValue | date}}
        </span>

        <v-spacer />

        <v-chip v-if="item.subscriptions && item.subscriptions.length" color="defaultGrey" text-color="secondary" x-small>
          <v-icon class="mr-1" x-small>mdi-autorenew</v-icon>{{item.subscriptions.length}}
        </v-chip>
      </v-card-actions>
    </v-card>
  </v-hover>
</template>
<script>
import GenericViewMixin from '@/mixins/GenericViewMixin'

const STATUS_STYLES = {
  pending: { label: 'Pendiente de envío', color: 'amber lighten-5', textColor: 'amber darken-4' },
  sent: { label: 'Enviada', color: 'lightBlue', textColor: 'primary' },
  overdue: { label: 'Atrasada', color: 'red lighten-5', textColor: 'red' },
  fulfilled: { label: 'Recibido', color: 'indigo lighten-5', textColor: 'indigo accent-4' },
  active: { label: 'Vigente', color: 'green lighten-5', textColor: 'green accent-4' },
  consumed: { label: 'Facturado', color: 'defaultGrey', textColor: 'secondary' },
  expired: { label: 'Expirado', color: 'red lighten-5', textColor: 'red' },
  archived: { label: 'Archivado', color: 'defaultGrey', textColor: 'secondary' },
  cancelled: { label: 'Cancelado', color: 'defaultGrey', textColor: 'secondary' }
}

export default {
  mixins: [
    GenericViewMixin
  ],
  props: {
    item: {
      type: Object,
      required: true
    }
  },
  computed: {
    customerName () {
      return this.item.customer_name || (this.item.customer && this.item.customer.name) || 'Sin cliente'
    },
    iconName () {
      if (this.item.kind === 'document') {
        if (this.item.document_type === '802') {
          return 'mdi-file-sign'
        }

        return 'mdi-file-document-check-outline'
      }

      if (this.item.submission_type === 'hes') {
        return 'mdi-clipboard-text-outline'
      }

      return 'mdi-file-document-outline'
    },
    typeLabel () {
      if (this.item.kind === 'document') {
        if (this.item.document_type === '801') {
          return 'Orden de compra'
        }

        if (this.item.document_type === '802') {
          return 'Contrato'
        }

        return ''
      }

      if (this.item.submission_type === 'order') {
        return 'Solicitud de OC'
      }

      if (this.item.submission_type === 'hes') {
        return 'Solicitud de HES'
      }

      return ''
    },
    statusStyle () {
      return STATUS_STYLES[this.item.status] || { label: this.item.status, color: 'defaultGrey', textColor: 'secondary' }
    },
    actions () {
      if (this.item.kind === 'request') {
        if (this.item.status === 'pending') {
          return [
            { id: 'send', label: 'Enviar solicitud' },
            { id: 'cancel', label: 'Cancelar', destructive: true }
          ]
        }

        if (this.item.status === 'sent' || this.item.status === 'overdue') {
          return [
            { id: 'upload', label: 'Importar documento recibido' },
            { id: 'resend', label: 'Reenviar email' },
            { id: 'cancel', label: 'Cancelar', destructive: true }
          ]
        }
      }

      if (this.item.kind === 'document') {
        if (this.item.status === 'active') {
          return [
            { id: 'view_pdf', label: 'Ver PDF' },
            { id: 'archive', label: 'Archivar', destructive: true }
          ]
        }

        if (['consumed', 'expired', 'archived'].includes(this.item.status)) {
          return [{ id: 'view_pdf', label: 'Ver PDF' }]
        }
      }

      return []
    },
    dateLabel () {
      if (this.item.kind === 'document') {
        return this.item.valid_until ? 'Vence el' : ''
      }

      return this.item.due_date ? 'Vence el' : ''
    },
    dateValue () {
      if (this.item.kind === 'document') {
        return this.item.valid_until
      }

      return this.item.due_date
    }
  }
}
</script>
<style scoped>
  .kanban-card {
    cursor: default;
    transition: ease-in-out;
  }
</style>
